/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import PhCheckCircle from 'ember-phosphor-icons/components/ph-check-circle';
import PhClock from 'ember-phosphor-icons/components/ph-clock';
import PhXCircle from 'ember-phosphor-icons/components/ph-x-circle';
import { DisputeStatus } from 'uplisting-frontend/models/dispute';
import { formatDate } from 'uplisting-frontend/helpers/date-format';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  disputes: ModelRegistry['dispute'][];
}

export interface BookingStatusDisputeSignature {
  Element: null;

  Args: IArgs;
}

export const RESPOND_BY_FORMAT = 'MMM do, yyyy @ h:mm a';

type AlertClass =
  | 'inline-alert-danger'
  | 'inline-alert-processing'
  | 'inline-alert-success';

type IconComponent = typeof PhXCircle | typeof PhClock | typeof PhCheckCircle;

interface IDisputeAlert {
  dispute: ModelRegistry['dispute'];
  alertClass: AlertClass;
  iconComponent: IconComponent;
  amount: string;
  respondBy: string;
}

function alertClass(dispute: ModelRegistry['dispute']): AlertClass {
  switch (dispute.status) {
    case DisputeStatus.needsResponse:
    case DisputeStatus.lost:
      return 'inline-alert-danger';
    case DisputeStatus.underReview:
      return 'inline-alert-processing';
    case DisputeStatus.won:
    case DisputeStatus.closed:
    case DisputeStatus.prevented:
      return 'inline-alert-success';
  }
}

function iconComponent(dispute: ModelRegistry['dispute']): IconComponent {
  switch (alertClass(dispute)) {
    case 'inline-alert-danger':
      return PhXCircle;
    case 'inline-alert-processing':
      return PhClock;
    case 'inline-alert-success':
      return PhCheckCircle;
  }
}

export default class UiBookingStatusDisputeComponent extends Component<BookingStatusDisputeSignature> {
  @service intl!: Services['intl'];

  @cached
  get alerts(): IDisputeAlert[] {
    return this.args.disputes.map((dispute) => ({
      dispute,
      alertClass: alertClass(dispute),
      iconComponent: iconComponent(dispute),
      amount: this.intl.formatNumber(dispute.amount, {
        style: 'currency',
        currency: dispute.currency,
      }),
      respondBy: dispute.respondBy
        ? formatDate(dispute.respondBy, RESPOND_BY_FORMAT)
        : '',
    }));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Status::Dispute': typeof UiBookingStatusDisputeComponent;
  }
}
